import React from 'react';
import { formatSpeed } from '../utils/statistics';

interface Props {
  label: string;
  speed: number | null;
  direction: 'download' | 'upload';
  isActive: boolean;
}

export const SpeedGauge: React.FC<Props> = ({ label, speed, direction, isActive }) => {
  const maxSpeed = 1000; // 1 Gbps scale
  const normalizedSpeed = speed ? Math.min(speed / maxSpeed, 1) : 0;
  const circumference = 2 * Math.PI * 60;
  const halfCircumference = circumference / 2;
  const offset = halfCircumference * (1 - normalizedSpeed);

  const gradientId = `gauge-${direction}`;
  const color = direction === 'download' ? '#22c55e' : '#3b82f6';
  const bgColor = direction === 'download' ? '#064e3b' : '#1e3a5f';

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700 flex flex-col items-center">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
        {label}
      </h3>

      <div className="relative w-40 h-24">
        <svg viewBox="0 0 140 80" className="w-full h-full">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={bgColor} />
              <stop offset="100%" stopColor={color} />
            </linearGradient>
          </defs>

          {/* Background arc */}
          <path
            d="M 10 70 A 60 60 0 0 1 130 70"
            fill="none"
            stroke="#1e293b"
            strokeWidth="10"
            strokeLinecap="round"
          />

          {/* Value arc */}
          <path
            d="M 10 70 A 60 60 0 0 1 130 70"
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${halfCircumference}`}
            strokeDashoffset={offset}
            className="transition-all duration-500 ease-out"
          />

          {/* Tick marks */}
          {[0, 0.25, 0.5, 0.75, 1].map((p) => {
            const angle = Math.PI + p * Math.PI;
            const x1 = 70 + 52 * Math.cos(angle);
            const y1 = 70 + 52 * Math.sin(angle);
            const x2 = 70 + 46 * Math.cos(angle);
            const y2 = 70 + 46 * Math.sin(angle);
            return (
              <line
                key={p}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#475569"
                strokeWidth="1"
              />
            );
          })}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-end pb-0">
          {speed !== null ? (
            <>
              <span className="text-2xl font-bold text-white">
                {speed >= 100 ? speed.toFixed(0) : speed.toFixed(1)}
              </span>
              <span className="text-[10px] text-slate-400 -mt-0.5">
                {speed >= 1000 ? 'Gbps' : 'Mbps'}
              </span>
            </>
          ) : (
            <span className="text-lg text-slate-600">
              {isActive ? '...' : '—'}
            </span>
          )}
        </div>
      </div>

      {/* Scale labels */}
      <div className="flex justify-between w-full px-2 -mt-1">
        <span className="text-[9px] text-slate-600">0</span>
        <span className="text-[9px] text-slate-600">250</span>
        <span className="text-[9px] text-slate-600">500</span>
        <span className="text-[9px] text-slate-600">750</span>
        <span className="text-[9px] text-slate-600">1G</span>
      </div>
    </div>
  );
};
